import { groupBy, isNil, pick, uniq, isNumber, isNaN } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import Card from '../../../components/card/Card'
import ProgressBar from '../../../components/progress/ProgressBar'
import PiezometerStationAction from '../../../station/actions/PiezometerStationAction'
import StationAction from '../../../station/actions/StationAction'
import DtoMeasureStats from '../../../station/dto/piezometer/DtoMeasureStats'
import DtoPiezoThreshold from '../../../station/dto/piezometer/DtoPiezoThreshold'
import { getLocalStorageJson, setLocalStorageJson } from '../../../utils/FormUtils'
import { getMeasuresTempRef, getOffsetFromTempRef } from '../../../utils/TempRefUtil'
import { arrayOf, instanceOf } from '../../../utils/StoreUtils'
import PiezometryAction from '../../actions/PiezometryAction'
import { HIDDEN_CHARTS, MEASURE_COTE, PIEZO_L_STORAGE } from '../../constants/PiezometryConstants'
import DtoParametrageDataType from '../../dto/DtoParametrageDataType'
import PiezometryExploitedChartPanel from './PiezometryExploitedChartPanel'
import DtoPiezometer from '../../dto/DtoPiezometer'
import AppStore from '../../../store/AppStore'
import IAEauAction from '../../../iaeau/IAEauAction'
import { hasBooleanValue } from '../../../utils/NumberUtil'
import AnalysisAction from '../../../quality/actions/AnalysisAction'
import DtoAssociatedStation from '../../../station/dto/DtoAssociatedStation'
import DtoAnalysisLight from '../../../quality/dto/analyse/DtoAnalysisLight'
import { STATION_TYPE_NAME } from '../../../station/constants/StationConstants'
import { getQualificationSelectOptions, getStatusSelectOptions } from '../../../utils/StatusUtil'
import DtoPiezoChartMeasures from 'piezometry/dto/chart/DtoPiezoChartMeasures'
import { Grid } from '@mui/material'
import MUIChartTabs from '../../../components/echart/MUIChartTabs'
import ChartTabPredData from '../../../station/components/iaeauModels/ChartTabPredData'
import ChartOptionsChip from '../../../components/echart/ChartOptionsChip'
import ChartTempRefChip from '../../../components/echart/ChartTempRefChip'
import DtoSandreCode from '../../../referencial/dto/DtoSandreCode'
import { CIVIL_YEAR, HISTO, HYDRO_YEAR, J90 } from '../../../alerting/constants/ChartFollowContants'
import { CHART_SELECTED_TIME, CHART_SELECTED_TIME_OTHER, OTHER } from '../../../quality/constants/ChartConstant'

// TODO ATTENTION ! Composant réutilisé dans plusieurs écrans
class PiezometryExploitedPanel extends Component {
    constructor(props) {
        super(props)
        const storedHiddenCharts = getLocalStorageJson(HIDDEN_CHARTS) || {}
        const tmpDisplayCote = getLocalStorageJson(PIEZO_L_STORAGE.DISPLAY_COTE) || props.displayCote || MEASURE_COTE.DEPTH // cote de calcul de la hauteur d'eau affichée (Profondeur, NGF, sol, ...)
        const noSol = !props.piezometer.link_landmarks?.some(l => l.origin === 3) // pas de repère de type SOL = pas d'affichage possible en mode SOL
        const selectedTimeTmp = getLocalStorageJson(CHART_SELECTED_TIME) || J90
        const selectedTime = isNaN(parseInt(selectedTimeTmp)) ? ([HISTO, CIVIL_YEAR, HYDRO_YEAR].includes(selectedTimeTmp) ? J90 : selectedTimeTmp) : parseInt(selectedTimeTmp)
        const minDateFromPreviousTime = (() => {
            if (isNumber(selectedTime)) {
                if (selectedTime <= 2) {
                    return moment().subtract(selectedTime, 'days').valueOf()
                }
                return moment().subtract(selectedTime, 'days').startOf('day').valueOf()
            }
            return undefined
        })()
        const selectedOtherDates = selectedTime === OTHER ? (getLocalStorageJson(CHART_SELECTED_TIME_OTHER) ?? {}) : {}
        this.state = {
            time: selectedTime,
            minDate: selectedOtherDates.minDate ?? minDateFromPreviousTime,
            maxDate: selectedOtherDates.maxDate,
            hiddenCharts: storedHiddenCharts,
            displayCote: tmpDisplayCote === MEASURE_COTE.GROUND && noSol ? MEASURE_COTE.NGF : tmpDisplayCote,
            dataLoaded: false,
            progress: 0,
            displayModes: getLocalStorageJson(PIEZO_L_STORAGE.DISPLAY_MODES_DASHBOARD) ?? { // différents modes d'affichage disponibles
                auto: true,
                brute: false,
                min: false,
                max: false,
                average: false,
                // ici, il y a aussi les checkbox des modèles (la clé est le nom du modèle)
            },
            filterStatusQualif: getLocalStorageJson(PIEZO_L_STORAGE.STATUS_QUALIF_DASHBOARD) ?? { // filtre sur statut et qualification (fait côté back)
                qualifications: getQualificationSelectOptions().map(q => q.code),
                status: getStatusSelectOptions().map(s => s.code),
            },
            predMeasures: [],
            selectedPred: undefined,
            predDate: undefined,
            predStats: [],
            qualitoMeasures: [],
            stats: [],
            fullScreen: false,
            tempRef: getLocalStorageJson(PIEZO_L_STORAGE.TEMP_REF) ?? undefined,
        }
    }

    componentDidMount() {
        if (!this.props.noLoad) {
            this.props.fetchPiezometer(this.props.id)
        }
        this.props.fetchPiezometerThresholds(this.props.id, MEASURE_COTE.NGF)
        this.props.fetchAssociatedSites(this.props.piezometer.code, 1)
            .then(() => this.props.fetchPiezoMeasuresStats(parseInt(this.props.id)))
            .then(r => {
                this.setState({ stats: r }, () => this.loadData(this.state))
                const statsFiltered = r.filter(stat => !hasBooleanValue(stat.showData) || stat.showData)
                const idsNitrates = statsFiltered.map(stat => {
                    return statsFiltered.find(h => h.typeId === stat.typeId)?.codeParameter
                }).filter(m => !isNil(m)).filter(p => !(p === ''))
                const assIdsQualito = this.props.associatedSites.filter(asso => asso.typeName === STATION_TYPE_NAME.quality).map(att => att.stationLinkedId)
                this.props.fetchGenericAnalysis({
                    lightMode: true,
                    stations: assIdsQualito,
                    parameters: idsNitrates,
                    support: '3', // support 'eau'
                }).then(json => this.setState({ qualitoMeasures: groupBy(json.map(a => new DtoAnalysisLight(a)), STATION_TYPE_NAME.qualitometer) }))
            })
        if (this.props.piezometerThresholds.length) {
            this.setState({ piezometerThresholds: this.props.piezometerThresholds.filter(t => t.code === this.props.piezometer.code) })
        }
        if (!this.props.piezometryDataTypes.length) {
            this.props.fetchPiezometryDataTypes()
        }
        this.props.fetchPiezometerChartOptions(this.props.id)
        AppStore.dispatch(IAEauAction.getModels('piezometry', parseInt(this.props.id)))
    }

    getStationTempRefOffset = () => getMeasuresTempRef(this.props.piezometer?.link_tempRefs, this.props.piezometerStatistics.map(stat => stat.typeId), this.props.sandreCodes)

    // Le fuseau choisi dans le sélecteur prime sur la référence déclarée sur la station. Il pilote
    // à la fois les regroupements demandés au back et la restitution des dates dans le graphique.
    getTempRefOffset = () => {
        const { tempRef } = this.state
        return isNil(tempRef) ? this.getStationTempRefOffset() : getOffsetFromTempRef(tempRef, this.props.sandreCodes)
    }

    changeTempRef = (tempRef) => {
        setLocalStorageJson(PIEZO_L_STORAGE.TEMP_REF, tempRef ?? null)
        this.setState({ tempRef }, () => this.loadData(pick(this.state, ['minDate', 'maxDate'])))
    }

    loadData = (dates) => {
        const { filterStatusQualif: { qualifications, status } } = this.state
        const piezoStat = this.props.piezometerStatistics.find(p => p.typeId === -1) || {}
        const fetchDates = { startDate: dates.minDate ? moment(dates.minDate).subtract(6, 'hours').valueOf() : moment().year(1960).valueOf(), endDate: dates.maxDate }
        const duration = piezoStat ? moment.duration(moment(fetchDates.endDate || moment().valueOf()).diff(fetchDates.startDate)) : null
        const autoDisplayMode = duration ? (duration.as('days') >= 91 ? 'MAX' : 'RAW') : 'MAX'
        const sampleDisplayMode = duration ? (duration.as('days') >= 91 ? 'SUM' : 'RAW') : 'SUM'

        const baseInput = { ...fetchDates, stationId: parseInt(this.props.id), tempRefOffsetHours: this.getTempRefOffset() }

        const modes = this.state.displayModes
        const piezoModes = uniq([
            modes.brute ? 'RAW' : (modes.auto ? autoDisplayMode : null),
            modes.max ? 'MAX' : null,
            modes.min ? 'MIN' : null,
            modes.average ? 'AVERAGE' : null,
            modes.personalizedGrouping ? modes.personalizedGroupingValue : null,
        ].filter(m => !!m))
        const piezo = piezoModes.map(p => ({
            ...baseInput,
            displayCote: this.state.displayCote,
            groupFunc: p,
            qualifications,
            status,
            dataType: -1,
            distinctByCodePoint: true,
        }))

        const sampleFound = this.props.piezometerStatistics.find(p => p.typeId === -2)
        const sample = !sampleFound ? [] : [{
            ...baseInput,
            dataType: -2,
            qualifications,
            status,
            groupFunc: sampleDisplayMode,
            distinctByCodePoint: true,
        }]

        const other = this.props.piezometerStatistics.filter(p => p.typeId > 0).flatMap(ps => piezoModes.map(pm => ({
            ...baseInput,
            dataType: ps.typeId,
            groupFunc: pm,
            qualifications,
            status,
            displayCote: ps.isPiezo ? this.state.displayCote : null,
            distinctByCodePoint: true,
        })))

        const aditionnalData = this.props.noAdditionnalData ? [] : [...sample, ...other]

        this.setState({ ...dates, dataLoaded: false, progress: 0 })

        this.props.loadPiezoChartMeasures([...piezo, ...aditionnalData], p => this.setState({ progress: p })).then(() => {
            this.setState({ dataLoaded: true })
            this.props.setDataLoaded()
        })
    }

    changeDates = (dates, forced) => {
        if (this.state.minDate !== dates.minDate || this.state.maxDate !== dates.maxDate || forced) {
            this.loadData(dates)
        }
    }

    getProgressBar = () => (
        <Card>
            <div className='padding-top-7 padding-left-2 padding-right-2 padding-bottom-2'>
                <ProgressBar progress={this.state.progress} withMessage />
            </div>
        </Card>
    )

    getPanel = () => {
        if (this.state.dataLoaded && this.props.piezometer) {
            const picked = pick(this.state, ['minDate', 'maxDate', 'hiddenCharts', 'displayCote', 'displayModes', 'predMeasures',
                'selectedPred', 'predStats', 'qualitoMeasures', 'stats', 'chartTab', 'fullScreen'])
            return <PiezometryExploitedChartPanel {...picked} withReturnPeriods={this.props.withReturnPeriods} tempRefOffsetHours={this.getTempRefOffset()} initialZoomValueStart={this.props.initialZoomValueStart} initialZoomValueEnd={this.props.initialZoomValueEnd} setFullScreen={(v)=> this.setState(({ fullScreen: v }))} />
        }
        return this.getProgressBar()
    }

    render() {
        const { time, displayModes, displayCote, filterStatusQualif, fullScreen, tempRef } = this.state
        const { id, piezometer, piezometerStatistics, useFromSituation, isPopin } = this.props

        const fullScreenStyle = (fullScreen) ? {
            position: 'fixed',
            paddingTop: '1rem',
            top: '64px',
            left: 0,
            height: !isPopin ? `calc(${window.innerHeight}px)` : `calc(${window.innerHeight}px)`,
            width: `calc(${window.innerWidth}px)`,
            zIndex: 9,
            marginTop: isPopin ? '-64px' : '0', // $navbar-height => 64px
            overflowY: 'auto',
            backgroundColor: 'white',
        } : {}

        if (piezometerStatistics.length) {
            return (
                <Grid sx={{ ...fullScreenStyle }} className={fullScreen ? 'fullscreen-chart' : ''}>
                    <Grid container alignItems='center' style={{ paddingLeft: 48 }}>
                        <MUIChartTabs
                            offsetHours={this.getTempRefOffset()}
                            time={time}
                            onChangeTime={(changes) => this.changeDates(changes)}
                        />
                        <ChartTabPredData
                            stationType='piezometry'
                            id={id}
                            setParentModelData={changes => this.setState(changes)}
                            useFromSituation={useFromSituation}
                        />
                        <Grid sx={{ paddingLeft: 2 }}>
                            <ChartOptionsChip
                                displayModes={displayModes}
                                displayCote={displayCote}
                                filterStatusQualif={filterStatusQualif}
                                changeParent={changes => this.setState(changes, () => this.loadData(pick(this.state, ['minDate', 'maxDate'])))}
                                withReferenceLevel={true}
                                noSol={!piezometer.link_landmarks.some(l => l.origin === 3)} // pas de mode SOL
                                displayModesKey={PIEZO_L_STORAGE.DISPLAY_MODES_DASHBOARD}
                                filterStatusQualifKey={PIEZO_L_STORAGE.STATUS_QUALIF_DASHBOARD}
                            />
                        </Grid>
                        <Grid sx={{ paddingLeft: 1 }}>
                            <ChartTempRefChip
                                tempRef={tempRef}
                                offsetHours={this.getTempRefOffset()}
                                stationOffsetHours={this.getStationTempRefOffset()}
                                stationTempRefs={piezometer?.link_tempRefs}
                                onChange={this.changeTempRef}
                            />
                        </Grid>
                    </Grid>
                    {this.getPanel()}
                </Grid>
            )
        }
        return null
    }
}

PiezometryExploitedPanel.propTypes = {
    params: PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string,
    }),
    id: PropTypes.string,
    initialZoomValueStart: PropTypes.number, // zoom initial optionnel du graphe (dataZoom)
    initialZoomValueEnd: PropTypes.number,
    piezometer: instanceOf(DtoPiezometer),
    piezometryDataTypes: PropTypes.arrayOf(PropTypes.instanceOf(DtoParametrageDataType)),
    piezometerThresholds: PropTypes.arrayOf(PropTypes.instanceOf(DtoPiezoThreshold)),
    setDataLoaded: PropTypes.func,
    piezometerStatistics: arrayOf(DtoMeasureStats),
    sandreCodes: PropTypes.arrayOf(PropTypes.instanceOf(DtoSandreCode)),
    fetchPiezoMeasuresStats: PropTypes.func,
    fetchPiezometerChartOptions: PropTypes.func,
    fetchPiezometer: PropTypes.func,
    fetchPiezometryDataTypes: PropTypes.func,
    noAdditionnalData: PropTypes.bool, // permet de ne pas afficher les données complémentaires,
    displayCote: PropTypes.number, // mode d'affichage piézo par défaut
    depthMode: PropTypes.bool, // permet d'afficher uniquement le mode profondeur
    fetchPiezometerThresholds: PropTypes.func,
    showTitle: PropTypes.bool,
    loadPiezoChartMeasures: PropTypes.func,
    useFromSituation: PropTypes.bool, // permet de charger des données de prévision sur la situation (ne pas retirer)
    getAnalysis: PropTypes.func,
    associatedSites: PropTypes.arrayOf(PropTypes.instanceOf(DtoAssociatedStation)),
    fetchGenericAnalysis: PropTypes.func,
    fetchAssociatedSites: PropTypes.func,
    chartMeasures: PropTypes.arrayOf(PropTypes.instanceOf(DtoPiezoChartMeasures)),
    noLoad: PropTypes.bool,
    isPopin: PropTypes.bool,
    withReturnPeriods: PropTypes.bool,
}

const mapStateToProps = store => ({
    piezometer: store.StationReducer.piezometer,
    piezometryDataTypes: store.PiezometryReducer.piezometryDataTypes,
    piezometerThresholds: store.PiezometerStationReducer.piezometerThresholds,
    piezometerStatistics: store.PiezometerStationReducer.piezometerStatistics,
    sandreCodes: store.ReferencialReducer.sandreCodes,
    associatedSites: store.StationReducer.associatedSites,
    chartMeasures: store.PiezometerStationReducer.chartMeasures,
})

const mapDispatchToProps = {
    fetchPiezoMeasuresStats: PiezometerStationAction.fetchPiezoMeasuresStats,
    fetchPiezometer: StationAction.fetchPiezometer,
    loadPiezoChartMeasures: PiezometerStationAction.loadPiezoChartMeasures,
    fetchPiezometryDataTypes: PiezometryAction.fetchPiezometryDataTypes,
    fetchPiezometerChartOptions: PiezometerStationAction.fetchPiezometerChartOptions,
    fetchPiezometerThresholds: PiezometerStationAction.fetchPiezometerThresholds,
    fetchGenericAnalysis: AnalysisAction.fetchGenericAnalysis,
    fetchAssociatedSites: StationAction.fetchAssociatedSites,
}

PiezometryExploitedPanel.defaultProps = {
    setDataLoaded: () => { },
    showTitle: true,
}

export default connect(mapStateToProps, mapDispatchToProps)(PiezometryExploitedPanel)
